import type { JSX } from "react";
import type { RunProgress } from "../../api/sourceStatus";

/**
 * Progress bar for one in-flight bootstrap, rendered from the typed
 * `RunProgress` union ONLY (`surfaceProgress`) — the raw SyncRun never reaches
 * this component, so an envelope counter (`fetched`/`ingested`) is
 * STRUCTURALLY incapable of meeting a primary-item `total` here (the live
 * "27979 / 2593+" bug class).
 *
 *   - determinate   → "done / total(+)" with a clamped [0,100]% bar
 *                     (done = discovered, SAME units as total — DEC-2).
 *   - indeterminate → pulsing bar with "N synced…" (N = discovered).
 *   - none          → nothing (the surface badge already carries the phase
 *                     label; no numeric pair exists — DEC-3).
 *
 * @tested-by: tst_fe_unit_accounts_025/031, tst_fe_unit_syncbar_001..006
 */
export function SyncProgressBar({ progress }: { progress: RunProgress }): JSX.Element | null {
  switch (progress.kind) {
    case "none":
      return null;
    case "indeterminate":
      return (
        <div className="mt-1 pl-3.5">
          <div className="flex items-center justify-between text-[11px] text-content-muted mb-0.5">
            <span>{progress.discovered} synced…</span>
          </div>
          <div className="h-1 rounded-full bg-surface-tertiary overflow-hidden">
            <div
              data-testid="sync-progress-indeterminate"
              className="h-full w-1/3 rounded-full bg-blue-400 animate-pulse"
            />
          </div>
        </div>
      );
    case "determinate": {
      const pct = Math.min(100, Math.max(0, (progress.done / progress.total) * 100));
      const suffix = progress.atLeast ? "+" : "";
      return (
        <div className="mt-1 pl-3.5">
          <div className="flex items-center justify-between text-[11px] text-content-muted mb-0.5">
            <span>
              {progress.done} / {progress.total}
              {suffix}
            </span>
          </div>
          <div className="h-1 rounded-full bg-surface-tertiary overflow-hidden">
            <div
              data-testid="sync-progress-bar"
              className="h-full rounded-full bg-blue-400 transition-all"
              style={{ width: `${String(pct)}%` }}
            />
          </div>
        </div>
      );
    }
    default: {
      const never: never = progress;
      throw new Error(`unknown progress kind ${JSON.stringify(never)}`);
    }
  }
}
